import { useState } from 'react'
import { ChevronUp, MessageSquare } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import {
  featureRequestService,
  categoryLabels,
  statusConfig,
  type FeatureRequestDto,
  type FeatureRequestStatus,
} from '@/lib/api/services/featureRequests'

/** Status tag boxes in the brand palette (square, no tints). */
const statusTag: Record<FeatureRequestStatus, string> = {
  Submitted: 'bg-[var(--color-landing-gray-100)] text-[var(--color-landing-gray-700)] dark:bg-white/10 dark:text-[var(--color-landing-gray-300)]',
  UnderReview: 'bg-[var(--color-landing-accent)] text-white',
  Planned: 'bg-[var(--color-landing-secondary)] text-white',
  InProgress: 'bg-[#ff00f2] text-white',
  Completed: 'bg-[#00d722] text-[var(--color-landing-primary)]',
  Declined: 'bg-[var(--color-landing-primary)] text-white dark:bg-white dark:text-[var(--color-landing-primary)]',
}

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

/**
 * One feature request as a hairline row in the public roadmap list: a square
 * vote counter on the left (fills blue once voted), tag boxes, title, two-line
 * excerpt and meta. Same voting behaviour as the dashboard card: signed-out
 * visitors are sent to sign in, signed-in votes toggle through the API.
 */
export function FeatureRequestRow({
  feature,
  isAuthenticated,
  onVoteChange,
  onLoginRequired,
}: {
  feature: FeatureRequestDto
  isAuthenticated: boolean
  onVoteChange?: () => void
  onLoginRequired?: () => void
}) {
  const [voteCount, setVoteCount] = useState(feature.voteCount)
  const [hasVoted, setHasVoted] = useState(feature.hasVoted || false)
  const [isVoting, setIsVoting] = useState(false)
  const { toast } = useToast()

  const handleVote = async () => {
    if (!isAuthenticated) {
      onLoginRequired?.()
      return
    }

    setIsVoting(true)
    try {
      const result = await featureRequestService.toggleVote(feature.id)
      setVoteCount(result.newVoteCount)
      setHasVoted(result.voted)
      onVoteChange?.()
    } catch (error) {
      toast({
        title: 'Vote failed',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsVoting(false)
    }
  }

  return (
    <article className="group grid grid-cols-[4rem_minmax(0,1fr)] gap-5 border-b border-[var(--color-landing-gray-200)] py-8 dark:border-[var(--color-landing-gray-800)] sm:grid-cols-[5rem_minmax(0,1fr)] sm:gap-8">
      <button
        type="button"
        onClick={handleVote}
        disabled={isVoting}
        aria-pressed={hasVoted}
        aria-label={`${hasVoted ? 'Remove vote from' : 'Vote for'} ${feature.title}, ${voteCount} vote${voteCount === 1 ? '' : 's'}`}
        className={cn(
          'flex h-16 w-16 flex-col items-center justify-center border transition-colors duration-200 disabled:opacity-60 sm:h-20 sm:w-20',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-landing-accent)]',
          hasVoted
            ? 'border-[var(--color-landing-accent)] bg-[var(--color-landing-accent)] text-white'
            : 'border-[var(--color-landing-gray-900)] text-[var(--color-landing-gray-900)] hover:border-[var(--color-landing-accent)] hover:text-[var(--color-landing-accent)] dark:border-white/50 dark:text-white dark:hover:border-white'
        )}
      >
        <ChevronUp className="h-5 w-5" strokeWidth={2.25} aria-hidden />
        <span className="font-[family-name:var(--font-landing-subdisplay)] text-xl font-bold leading-none tabular-nums">{voteCount}</span>
      </button>

      <div className="min-w-0">
        <div className="mb-3 flex flex-wrap gap-2">
          <span className="border border-[var(--color-landing-gray-300)] px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-[var(--color-landing-gray-700)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]">
            {categoryLabels[feature.category]}
          </span>
          <span className={cn('px-2 py-0.5 text-xs font-bold uppercase tracking-wide', statusTag[feature.status])}>
            {statusConfig[feature.status].label}
          </span>
        </div>

        <h3 className="line-clamp-2 font-[family-name:var(--font-landing-subdisplay)] text-xl font-semibold leading-snug tracking-[-0.01em] text-[var(--color-landing-gray-900)] dark:text-white lg:text-2xl">
          {feature.title}
        </h3>
        <p className="mt-2 line-clamp-2 max-w-[75ch] landing-text-body-sm text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
          {feature.description}
        </p>

        <p className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
          {feature.submittedByName && <span>{feature.submittedByName}</span>}
          <time dateTime={feature.createdAt}>{formatDate(feature.createdAt)}</time>
        </p>

        {feature.adminResponse && (
          <div className="mt-5 max-w-[75ch] border border-[var(--color-landing-gray-200)] bg-[var(--color-landing-gray-50)] p-4 dark:border-[var(--color-landing-gray-800)] dark:bg-white/[0.03]">
            <p className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-[var(--color-landing-gray-900)] dark:text-white">
              <MessageSquare className="h-4 w-4 text-[var(--color-landing-accent)] dark:text-[#8b85ff]" aria-hidden />
              Team response
            </p>
            <p className="text-sm text-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]">{feature.adminResponse}</p>
          </div>
        )}
      </div>
    </article>
  )
}
